const cache = new Map<string, Filter>();
/**
 * Pulled from https://codepen.io/sosuke/pen/Pjoqqp
 */
export class Color {
	rgbValues: RGB;

	constructor(color: string) {
		this.rgbValues = hexToRgb(color);
	}

	static fromString(color: string) {
		return new Color(getColorFromString(color));
	}

	public toHex() {
		return `#${this.rgbValues.r.toString(16).padStart(2, "0")}${this.rgbValues.g.toString(16).padStart(2, "0")}${this.rgbValues.b.toString(16).padStart(2, "0")}`;
	}

	public toString() {
		return `rgb(${Math.round(this.rgbValues.r)}, ${Math.round(this.rgbValues.g)}, ${Math.round(this.rgbValues.b)})`;
	}

	hueRotate(angle = 0) {
		angle = (angle / 180) * Math.PI;
		const sin = Math.sin(angle);
		const cos = Math.cos(angle);

		this.multiply([
			0.213 + cos * 0.787 - sin * 0.213,
			0.715 - cos * 0.715 - sin * 0.715,
			0.072 - cos * 0.072 + sin * 0.928,
			0.213 - cos * 0.213 + sin * 0.143,
			0.715 + cos * 0.285 + sin * 0.14,
			0.072 - cos * 0.072 - sin * 0.283,
			0.213 - cos * 0.213 - sin * 0.787,
			0.715 - cos * 0.715 + sin * 0.715,
			0.072 + cos * 0.928 + sin * 0.072,
		]);
	}

	grayscale(value = 1) {
		this.multiply([
			0.2126 + 0.7874 * (1 - value),
			0.7152 - 0.7152 * (1 - value),
			0.0722 - 0.0722 * (1 - value),
			0.2126 - 0.2126 * (1 - value),
			0.7152 + 0.2848 * (1 - value),
			0.0722 - 0.0722 * (1 - value),
			0.2126 - 0.2126 * (1 - value),
			0.7152 - 0.7152 * (1 - value),
			0.0722 + 0.9278 * (1 - value),
		]);
	}

	sepia(value = 1) {
		this.multiply([
			0.393 + 0.607 * (1 - value),
			0.769 - 0.769 * (1 - value),
			0.189 - 0.189 * (1 - value),
			0.349 - 0.349 * (1 - value),
			0.686 + 0.314 * (1 - value),
			0.168 - 0.168 * (1 - value),
			0.272 - 0.272 * (1 - value),
			0.534 - 0.534 * (1 - value),
			0.131 + 0.869 * (1 - value),
		]);
	}

	saturate(value = 1) {
		this.multiply([
			0.213 + 0.787 * value,
			0.715 - 0.715 * value,
			0.072 - 0.072 * value,
			0.213 - 0.213 * value,
			0.715 + 0.285 * value,
			0.072 - 0.072 * value,
			0.213 - 0.213 * value,
			0.715 - 0.715 * value,
			0.072 + 0.928 * value,
		]);
	}

	multiply(matrix: number[]) {
		const newR = clamp(this.rgbValues.r * matrix[0] + this.rgbValues.g * matrix[1] + this.rgbValues.b * matrix[2]);
		const newG = clamp(this.rgbValues.r * matrix[3] + this.rgbValues.g * matrix[4] + this.rgbValues.b * matrix[5]);
		const newB = clamp(this.rgbValues.r * matrix[6] + this.rgbValues.g * matrix[7] + this.rgbValues.b * matrix[8]);
		this.rgbValues = { r: newR, g: newG, b: newB };
	}

	brightness(value = 1) {
		this.linear(value);
	}
	contrast(value = 1) {
		this.linear(value, -(0.5 * value) + 0.5);
	}

	linear(slope = 1, intercept = 0) {
		this.rgbValues.r = clamp(this.rgbValues.r * slope + intercept * 255);
		this.rgbValues.g = clamp(this.rgbValues.g * slope + intercept * 255);
		this.rgbValues.b = clamp(this.rgbValues.b * slope + intercept * 255);
	}

	invert(value = 1) {
		this.rgbValues.r = clamp((value + (this.rgbValues.r / 255) * (1 - 2 * value)) * 255);
		this.rgbValues.g = clamp((value + (this.rgbValues.g / 255) * (1 - 2 * value)) * 255);
		this.rgbValues.b = clamp((value + (this.rgbValues.b / 255) * (1 - 2 * value)) * 255);
	}

	public hsl() {
		// Code taken from https://stackoverflow.com/a/9493060/2688027, licensed under CC BY-SA.
		const r = this.rgbValues.r / 255;
		const g = this.rgbValues.g / 255;
		const b = this.rgbValues.b / 255;
		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		let h,
			s,
			l = (max + min) / 2;

		if (max === min) {
			h = s = 0;
		} else {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;

				case g:
					h = (b - r) / d + 2;
					break;

				case b:
					h = (r - g) / d + 4;
					break;
			}
			h /= 6;
		}

		return {
			h: h * 100,
			s: s * 100,
			l: l * 100,
		};
	}
}

export function getFilterValuesForHex(targetHex: string, baseColor = "#000000") {
	const target = new Color(targetHex);
	const targetHSL = target.hsl();
	if (cache.has(targetHex)) {
		return { values: cache.get(targetHex), loss: 0 };
	}
	return solve();

	function solve() {
		const result = solveNarrow(solveWide());
		cache.set(targetHex, result.values ?? ({} as any));
		return {
			values: result.values,
			loss: result.loss,
		};
	}

	function solveWide() {
		const A = 5;
		const c = 15;
		const a: Filter = {
			invert: 60,
			sepia: 180,
			saturate: 18000,
			hueRotate: 600,
			brightness: 1.2,
			contrast: 1.2,
		};

		let best = { loss: Infinity };
		for (let i = 0; best.loss > 25 && i < 3; i++) {
			const initial: Filter = {
				invert: 50,
				sepia: 20,
				saturate: 3750,
				hueRotate: 50,
				brightness: 100,
				contrast: 100,
			};
			const result = spsa(A, a, c, initial, 1000);
			if (result.loss < best.loss) {
				best = result;
			}
		}
		return best;
	}

	function solveNarrow(wide: { loss: number; values?: Filter }) {
		const A = wide.loss;
		const c = 2;
		const A1 = A + 1;
		const a: Filter = {
			invert: 0.25 * A1,
			sepia: 0.25 * A1,
			saturate: A1,
			hueRotate: 0.25 * A1,
			brightness: 0.2 * A1,
			contrast: 0.2 * A1,
		};
		return spsa(A, a, c, wide.values, 500);
	}

	function spsa(A: number, a: Filter, c: number, values: Filter, iters: number) {
		const alpha = 1;
		const gamma = 0.16666666666666666;

		let best: Filter = null;
		let bestLoss = Infinity;
		const deltas = {} as Filter;
		const highArgs = {} as Filter;
		const lowArgs = {} as Filter;

		for (let k = 0; k < iters; k++) {
			const ck = c / Math.pow(k + 1, gamma);
			const filterKeys = Object.keys(values) as (keyof Filter)[];
			for (let key of filterKeys) {
				deltas[key] = Math.random() > 0.5 ? 1 : -1;
				highArgs[key] = values[key] + ck * deltas[key];
				lowArgs[key] = values[key] - ck * deltas[key];
			}

			const lossDiff = calcLoss(highArgs) - calcLoss(lowArgs);
			for (let key of filterKeys) {
				const g = (lossDiff / (2 * ck)) * deltas[key];
				const ak = a[key] / Math.pow(A + k + 1, alpha);
				values[key] = fix(values[key] - ak * g, key);
			}

			const loss = calcLoss(values);
			if (loss < bestLoss) {
				best = { ...values };
				bestLoss = loss;
			}
		}
		return { values: best, loss: bestLoss };

		function fix(value: number, key: keyof Filter) {
			let max = 100;
			switch (key) {
				case "saturate":
					max = 7500;
					break;
				case "brightness":
				case "contrast":
					max = 200;
					break;
				case "hueRotate":
					if (value > max) value %= max;
					else if (value < 0) value = max + (value % max);
					return value;
			}
			if (value < 0) value = 0;
			else if (value > max) value = max;

			return value;
		}

		function calcLoss(filters: Filter) {
			const color = new Color(baseColor);

			color.invert(filters.invert / 100);
			color.sepia(filters.sepia / 100);
			color.saturate(filters.saturate / 100);
			color.hueRotate(filters.hueRotate * 3.6);
			color.brightness(filters.brightness / 100);
			color.contrast(filters.contrast / 100);

			const colorHSL = color.hsl();
			return (
				Math.abs(color.rgbValues.r - target.rgbValues.r) +
				Math.abs(color.rgbValues.g - target.rgbValues.g) +
				Math.abs(color.rgbValues.b - target.rgbValues.b) +
				Math.abs(colorHSL.h - targetHSL.h) +
				Math.abs(colorHSL.s - targetHSL.s) +
				Math.abs(colorHSL.l - targetHSL.l)
			);
		}
	}
}

export function getColorFromString(str: string): string {
	// Generate a hash from the string
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
		hash = hash & hash;
	}

	// Mix the bits
	hash = ((hash << 13) ^ hash) * 0x45d9f3b;
	hash = ((hash << 13) ^ hash) * 0x45d9f3b;
	hash = (hash << 13) ^ hash;

	// Get base values
	let r = Math.abs((hash & 0xff0000) >> 16);
	let g = Math.abs((hash & 0x00ff00) >> 8);
	let b = Math.floor(Math.abs(hash & 0x0000ff) * 0.25);

	// Ensure we have at least one strong component
	const maxComponent = Math.max(r, g, b);
	if (maxComponent < 100) {
		const scale = 100 / maxComponent;
		r = Math.floor(r * scale);
		b = Math.floor(b * scale);
	}

	return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function hexToRgb(hex: string) {
	let r = parseInt(hex.slice(1, 3), 16);
	let g = parseInt(hex.slice(3, 5), 16);
	let b = parseInt(hex.slice(5, 7), 16);
	return { r, g, b };
}

function clamp(value: number) {
	if (value > 255) {
		value = 255;
	} else if (value < 0) {
		value = 0;
	}
	return value;
}

interface Filter {
	invert: number;
	sepia: number;
	saturate: number;
	hueRotate: number;
	brightness: number;
	contrast: number;
}
interface RGB {
	r: number;
	g: number;
	b: number;
}
